import { Check, CircleDot, Loader2, X } from 'lucide-react';

import { cn } from '~/utils/cn';

import { TONE_CLASSES, type StatusTone, statusTone } from '../utils/run-format';

/** Q19 — a tiny glyph per tone, so a verdict/outcome chip reads at a glance, not just by colour. */
const TONE_ICON: Partial<Record<StatusTone, typeof Check>> = {
  success: Check,
  waiting: CircleDot,
  failed: X,
  running: Loader2,
};

interface Props {
  /** Raw status code — picks the tone. */
  status: string;
  /** Text to show; the caller resolves the label (and any "· Stage" suffix). */
  label: string;
  /** Prepend the tone's glyph (✓ success, ◐ waiting, ✕ failed) — for verdict/tier/outcome
   *  chips (Q19); off by default so existing run/stage status chips keep their look. */
  showIcon?: boolean;
  className?: string;
}

/** Pill for a run / stage / delivery status, toned by the status code (one palette for every run screen). */
export function RunStatusChip({ status, label, showIcon = false, className }: Props) {
  const tone = statusTone(status);
  const Icon = showIcon ? TONE_ICON[tone] : undefined;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {Icon ? (
        <Icon aria-hidden="true" className={cn('size-3', tone === 'running' && 'animate-spin')} />
      ) : null}
      {label}
    </span>
  );
}
